/**
 * Zod validation helpers for form inputs.
 *
 * These utilities handle the common case where HTML inputs return strings,
 * but we want to validate/transform them as numbers with proper error messages.
 */

import { z } from 'zod'

// =============================================================================
// Transform Helpers
// =============================================================================

/**
 * Convert string/number input to number or undefined.
 * Treats empty strings, null, undefined, and NaN as undefined.
 */
export const toNumberOrUndefined = (val: unknown): number | undefined => {
  if (val === '' || val === null || val === undefined) return undefined
  const num = typeof val === 'number' ? val : Number(val)
  return isNaN(num) ? undefined : num
}

/**
 * Convert string/number input to positive number or undefined.
 * Treats empty strings, 0, negative numbers, and NaN as undefined.
 */
export const toPositiveNumberOrUndefined = (val: unknown): number | undefined => {
  if (val === '' || val === null || val === undefined) return undefined
  const num = typeof val === 'number' ? val : Number(val)
  if (isNaN(num) || num <= 0) return undefined
  return num
}

/**
 * Convert string/number input to non-negative number or undefined.
 * Treats empty strings, negative numbers, and NaN as undefined.
 * Allows 0 as a valid value.
 */
export const toNonNegativeNumberOrUndefined = (val: unknown): number | undefined => {
  if (val === '' || val === null || val === undefined) return undefined
  const num = typeof val === 'number' ? val : Number(val)
  if (isNaN(num) || num < 0) return undefined
  return num
}

// =============================================================================
// Zod Schemas
// =============================================================================

/** Input type union for number fields (handles string from HTML inputs) */
const numberInputUnion = z.union([z.string(), z.number(), z.undefined()])

/**
 * Zod number schema builders for form validation.
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   price: zodNumber.requiredPositive('Price is required'),
 *   beds: zodNumber.optionalNonNegative(),  // allows 0
 *   sqft: zodNumber.optionalPositive(),     // must be > 0 if provided
 *   rating: zodNumber.optional(),           // any number
 * })
 * ```
 */
export const zodNumber = {
  /**
   * Optional number field. Accepts any number, empty becomes undefined.
   */
  optional: () => numberInputUnion.transform(toNumberOrUndefined),

  /**
   * Optional positive number (must be > 0 if provided).
   * Empty or 0 becomes undefined.
   */
  optionalPositive: () => numberInputUnion.transform(toPositiveNumberOrUndefined),

  /**
   * Optional non-negative number (allows 0).
   * Empty or negative becomes undefined.
   */
  optionalNonNegative: () => numberInputUnion.transform(toNonNegativeNumberOrUndefined),

  /**
   * Required positive number with custom error message.
   */
  requiredPositive: (message: string) =>
    numberInputUnion
      .transform(toNumberOrUndefined)
      .refine((val): val is number => val !== undefined && val > 0, { message }),

  /**
   * Required non-negative number (allows 0) with custom error message.
   */
  requiredNonNegative: (message: string) =>
    numberInputUnion
      .transform(toNonNegativeNumberOrUndefined)
      .refine((val): val is number => val !== undefined && val >= 0, { message }),

  /**
   * Optional year number with range validation.
   * @param minYear - Minimum valid year (default: 1800)
   * @param maxYear - Maximum valid year (default: current year)
   */
  optionalYear: (minYear = 1800, maxYear = new Date().getFullYear()) =>
    numberInputUnion
      .transform(toPositiveNumberOrUndefined)
      .refine((val) => val === undefined || (val >= minYear && val <= maxYear), {
        message: `Year must be between ${minYear} and ${maxYear}`,
      }),

  /**
   * Required year number with range validation.
   * @param message - Error message when year is missing
   * @param minYear - Minimum valid year (default: 1800)
   * @param maxYear - Maximum valid year (default: current year)
   */
  requiredYear: (message: string, minYear = 1800, maxYear = new Date().getFullYear()) =>
    numberInputUnion
      .transform(toPositiveNumberOrUndefined)
      .refine((val): val is number => val !== undefined && val >= minYear && val <= maxYear, {
        message,
      }),
}
